import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { clampLines } from "./Styles";
import { throttle } from "throttle-debounce";

const Styled = {
  OuterContainer: styled.div`
    position: relative;
  `,
  ClampedContainer: styled.div<{
    lines: number;
    isExpanded: boolean;
  }>`
    ${(props) => !props.isExpanded && clampLines(props.lines)};
  `,
  // Display none will not allow us to detect whether we are in an overflow case
  // Also keep inline so that we are confident it is behaving similarly to the real content
  HiddenContent: styled.div.attrs({
    "aria-hidden": true,
  })`
    pointer-events: none;
    height: 0;
    opacity: 0;
  `,
};

const ExpandableTruncator: React.FC<{
  isExpanded: boolean;
  numLines: number;
  onExpandableStatus: (isExpandable: boolean) => void;
  className?: string;
}> = ({ isExpanded, numLines, onExpandableStatus, className, children }) => {
  const clampedContainerRef = useRef<HTMLDivElement>(null);
  const lastOverflow = useRef<boolean | undefined>();

  // TODO: This was identified as an issue during the react 17 upgrade
  //       Revisit next time we're in here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkOverflow = useCallback(
    throttle(300, () => {
      const el = clampedContainerRef.current;
      if (el) {
        const isOverflowing = el.scrollHeight > el.offsetHeight;
        if (lastOverflow.current !== isOverflowing) {
          lastOverflow.current = isOverflowing;
          onExpandableStatus(isOverflowing);
        }
      }
    }),
    [onExpandableStatus]
  );

  useLayoutEffect(checkOverflow, [checkOverflow]);

  useEffect(() => {
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow]);

  return (
    <Styled.OuterContainer className={className}>
      <Styled.HiddenContent>
        {/* Always-clamped hidden content that can be used for measurement */}
        <Styled.ClampedContainer
          lines={numLines}
          ref={clampedContainerRef}
          isExpanded={false}
        >
          {children}
        </Styled.ClampedContainer>
      </Styled.HiddenContent>
      <Styled.ClampedContainer lines={numLines} isExpanded={isExpanded}>
        {children}
      </Styled.ClampedContainer>
    </Styled.OuterContainer>
  );
};

export default ExpandableTruncator;
